import { createTheme } from '@mui/material/styles';
import { DEFAULT_PORTAL_MODE, getPortalTokens, PORTAL_FONT_FAMILY } from './styles/portalTheme';

/**
 * Build the app MUI theme for a color mode.
 * Wire `mode` from a future ThemeModeProvider to enable dark mode app-wide.
 *
 * @param {'light' | 'dark'} [mode]
 */
export function createAppTheme(mode = DEFAULT_PORTAL_MODE) {
  const portal = getPortalTokens(mode);

  return createTheme({
    portal,
    typography: {
      fontFamily: PORTAL_FONT_FAMILY,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontWeight: 300 },
      h2: { fontWeight: 300 },
      h3: { fontWeight: 400 },
      h4: { fontWeight: 400, marginBottom: '16px' },
      h5: { fontWeight: 400 },
      h6: { fontWeight: 500 },
      subtitle1: { fontWeight: 400 },
      subtitle2: { fontWeight: 400 },
      body1: { fontWeight: 400 },
      body2: { fontWeight: 400 },
      button: { fontWeight: 500 },
      caption: { fontWeight: 400 },
      overline: { fontWeight: 400 },
    },
    palette: {
      mode,
      primary: {
        main: portal.accent,
        dark: portal.accentHover,
        light: portal.accentSoft,
        contrastText: '#ffffff',
      },
      secondary: {
        main: portal.textMuted,
      },
      warning: {
        main: portal.accent,
      },
      success: {
        main: mode === 'dark' ? '#6BC67A' : '#2E7D32',
      },
      background: {
        default: portal.pageBg,
        paper: portal.surface,
      },
      text: {
        primary: portal.text,
        secondary: portal.textMuted,
      },
      divider: portal.border,
      action: {
        hover: portal.surfaceMuted,
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: portal.pageBg,
            color: portal.text,
            fontFamily: PORTAL_FONT_FAMILY,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 20,
          },
          containedPrimary: {
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: portal.accentHover,
              boxShadow: 'none',
            },
          },
          outlinedPrimary: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: portal.inputBorder,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            backgroundColor: portal.surface,
            borderBottom: `1px solid ${portal.border}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${portal.border}`,
            backgroundColor: portal.sidebarBg,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 600,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 400,
            fontSize: '1rem',
            '&.Mui-selected': {
              fontWeight: 700,
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: portal.border,
            whiteSpace: 'nowrap',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: portal.surface,
            backgroundImage: 'none',
            border: `1px solid ${portal.border}`,
            boxShadow: portal.cardShadow,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: 16,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: portal.surface,
            border: `1px solid ${portal.border}`,
            boxShadow: portal.cardShadow,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: portal.surface,
            border: `1px solid ${portal.border}`,
            boxShadow: portal.cardShadow,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: portal.surface,
            border: `1px solid ${portal.border}`,
            boxShadow: portal.cardShadow,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'dark' ? portal.surfaceMuted : portal.text,
            color: mode === 'dark' ? portal.text : '#ffffff',
            fontSize: '0.75rem',
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: {
            backgroundColor: portal.surface,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: portal.surfaceMuted,
            borderRadius: 3,
          },
        },
      },
    },
  });
}

const theme = createAppTheme(DEFAULT_PORTAL_MODE);

export default theme;
